<template>
  <div
    class="video-panel"
    :class="{
      'user-speaking': isUserSpeaking,
      'portrait-mode': isPortraitMode,
      'mount-only': mountOnly,
    }"
    ref="videoPanelRef"
  >
    <div
      class="video-container"
      :class="{ 'can-rotate': isConnected }"
      :data-rotation="rotationDeg"
      :title="
        isConnected
          ? `点击调整画面方向（当前 ${rotationDeg}°）`
          : undefined
      "
      @mousedown="startDrag"
      @click="handleVideoClick"
    >
      <div id="local-player"></div>
      <div v-if="!isConnected" class="video-placeholder">
        <VideoCameraOutlined class="camera-off-icon" />
        <p>点击"开启视频通话"开始</p>
      </div>
      <div
        v-if="isConnected && rotationHintVisible"
        class="rotation-hint"
        aria-live="polite"
      >
        {{ rotationDeg }}°
      </div>
    </div>
    <div class="video-controls" v-if="isPortraitMode">
      <a-button
        class="minimize-video-btn"
        shape="circle"
        @click.stop="$emit('minimize')"
      >
        <template #icon><MinusOutlined /></template>
      </a-button>
    </div>
    <div
      class="resize-handle"
      v-if="isPortraitMode"
      @mousedown="startResize"
    ></div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  watch,
} from 'vue';
import MinusOutlined from '@ant-design/icons-vue/MinusOutlined';
import VideoCameraOutlined from '@ant-design/icons-vue/VideoCameraOutlined';

/**
 * 视频面板组件
 * 负责显示本地视频和控制视频最小化
 */
export default defineComponent({
  name: 'VideoPanel',
  components: {
    MinusOutlined,
    VideoCameraOutlined,
  },
  props: {
    /**
     * 是否已连接到实时服务
     */
    isConnected: {
      type: Boolean,
      default: false,
    },
    /**
     * 用户是否正在说话
     */
    isUserSpeaking: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否为竖屏模式
     */
    isPortraitMode: {
      type: Boolean,
      default: false,
    },
    mountOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * 最小化视频事件
     */
    'minimize',
  ],
  setup(props) {
    const videoPanelRef = ref<HTMLElement | null>(null);
    const isDragging = ref(false);
    const isResizing = ref(false);
    const startX = ref(0);
    const startY = ref(0);
    const startWidth = ref(0);
    const startHeight = ref(0);
    const dragMoved = ref(false);
    const dragPointerX = ref(0);
    const dragPointerY = ref(0);

    // 触摸事件相关变量
    const isTouchDragging = ref(false);
    const isPinching = ref(false);
    const touchStartX = ref(0);
    const touchStartY = ref(0);
    const touchStartDistance = ref(0);
    const touchStartZoom = ref(1);
    const currentZoom = ref(1);

    const ROTATION_STEPS = [0, 90, 180, 270] as const;
    const ROTATION_STORAGE_KEY = 'camera-video-rotation-deg';

    const loadStoredRotation = (): number => {
      try {
        const raw = localStorage.getItem(ROTATION_STORAGE_KEY);
        const parsed = Number(raw);
        if (ROTATION_STEPS.includes(parsed as (typeof ROTATION_STEPS)[number])) {
          return parsed;
        }
      } catch {
        // ignore storage errors
      }
      return 0;
    };

    const rotationDeg = ref(loadStoredRotation());
    const rotationHintVisible = ref(false);
    let rotationHintTimer: number | null = null;
    let videoAttachObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let touchTapStartX = 0;
    let touchTapStartY = 0;
    let touchTapMoved = false;
    let suppressClickUntil = 0;

    const persistRotation = (deg: number): void => {
      try {
        localStorage.setItem(ROTATION_STORAGE_KEY, String(deg));
      } catch {
        // ignore storage errors
      }
    };

    const showRotationHint = (): void => {
      rotationHintVisible.value = true;
      if (rotationHintTimer !== null) {
        window.clearTimeout(rotationHintTimer);
      }
      rotationHintTimer = window.setTimeout(() => {
        rotationHintVisible.value = false;
        rotationHintTimer = null;
      }, 900);
    };

    /**
     * 应用镜像 + 旋转 + 缩放；90°/270° 额外放大以铺满容器
     */
    const applyVideoTransform = (): void => {
      const player = document.querySelector('#local-player') as HTMLElement | null;
      const videoElement = document.querySelector(
        '#local-player video'
      ) as HTMLElement | null;
      if (!videoElement || !player) {
        return;
      }

      const rotation = rotationDeg.value;
      const zoom = currentZoom.value;
      let coverScale = 1;

      if (rotation === 90 || rotation === 270) {
        const w = player.clientWidth || 1;
        const h = player.clientHeight || 1;
        // 旋转 90° 后宽高对调，放大到至少覆盖容器
        coverScale = Math.max(w / h, h / w);
      }

      videoElement.style.transform = `scaleX(-1) rotate(${rotation}deg) scale(${coverScale * zoom})`;
      videoElement.style.transformOrigin = 'center center';
      videoElement.style.objectFit = 'cover';
    };

    const cycleRotation = (): void => {
      const currentIndex = ROTATION_STEPS.indexOf(
        rotationDeg.value as (typeof ROTATION_STEPS)[number]
      );
      const nextIndex =
        currentIndex < 0 ? 0 : (currentIndex + 1) % ROTATION_STEPS.length;
      rotationDeg.value = ROTATION_STEPS[nextIndex];
      persistRotation(rotationDeg.value);
      applyVideoTransform();
      showRotationHint();
    };

    /**
     * 点击摄像头画面：每次点击旋转 90°（0→90→180→270→0）
     * 1 秒内连续点击可快速切过所有朝向
     */
    const handleVideoClick = (e: MouseEvent): void => {
      if (!props.isConnected || props.mountOnly) {
        return;
      }

      if (Date.now() < suppressClickUntil) {
        return;
      }

      // 竖屏拖动/缩放结束后不触发旋转
      if (dragMoved.value || isResizing.value || isPinching.value) {
        dragMoved.value = false;
        return;
      }

      const target = e.target as HTMLElement | null;
      if (target?.closest('.video-controls, .resize-handle, .minimize-video-btn')) {
        return;
      }

      cycleRotation();
    };

    /**
     * 开始拖动
     */
    const startDrag = (e: MouseEvent) => {
      if (!props.isPortraitMode) return;

      // 阻止事件冒泡，避免触发 resize
      e.stopPropagation();

      isDragging.value = true;
      dragMoved.value = false;
      dragPointerX.value = e.clientX;
      dragPointerY.value = e.clientY;
      startX.value = e.clientX - (videoPanelRef.value?.offsetLeft || 0);
      startY.value = e.clientY - (videoPanelRef.value?.offsetTop || 0);

      document.addEventListener('mousemove', drag);
      document.addEventListener('mouseup', stopDrag);
    };

    /**
     * 拖动中
     */
    const drag = (e: MouseEvent) => {
      if (!isDragging.value || !videoPanelRef.value) return;

      if (
        Math.abs(e.clientX - dragPointerX.value) > 4 ||
        Math.abs(e.clientY - dragPointerY.value) > 4
      ) {
        dragMoved.value = true;
      }

      const newX = e.clientX - startX.value;
      const newY = e.clientY - startY.value;

      // 限制在窗口范围内
      const maxX = window.innerWidth - (videoPanelRef.value.offsetWidth || 0);
      const maxY = window.innerHeight - (videoPanelRef.value.offsetHeight || 0);

      const clampedX = Math.max(0, Math.min(maxX, newX));
      const clampedY = Math.max(0, Math.min(maxY, newY));

      videoPanelRef.value.style.left = `${clampedX}px`;
      videoPanelRef.value.style.top = `${clampedY}px`;
    };

    /**
     * 停止拖动
     */
    const stopDrag = () => {
      isDragging.value = false;
      document.removeEventListener('mousemove', drag);
      document.removeEventListener('mouseup', stopDrag);
    };

    /**
     * 开始调整大小
     */
    const startResize = (e: MouseEvent) => {
      if (!props.isPortraitMode || !videoPanelRef.value) return;

      // 阻止事件冒泡，避免触发 drag
      e.stopPropagation();

      isResizing.value = true;
      startX.value = e.clientX;
      startY.value = e.clientY;
      startWidth.value = videoPanelRef.value.offsetWidth;
      startHeight.value = videoPanelRef.value.offsetHeight;

      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
    };

    /**
     * 调整大小中
     */
    const resize = (e: MouseEvent) => {
      if (!isResizing.value || !videoPanelRef.value) return;

      const deltaX = e.clientX - startX.value;
      const deltaY = e.clientY - startY.value;

      // 计算新的宽度和高度，保持宽高比
      const aspectRatio = startWidth.value / startHeight.value;
      const newWidth = Math.max(100, startWidth.value + deltaX);
      const newHeight = Math.max(75, newWidth / aspectRatio);

      // 限制最大尺寸
      const maxWidth = window.innerWidth * 0.5;
      const maxHeight = window.innerHeight * 0.5;
      const clampedWidth = Math.min(maxWidth, newWidth);
      const clampedHeight = Math.min(maxHeight, newHeight);

      videoPanelRef.value.style.width = `${clampedWidth}px`;
      videoPanelRef.value.style.height = `${clampedHeight}px`;
      applyVideoTransform();
    };

    /**
     * 停止调整大小
     */
    const stopResize = () => {
      isResizing.value = false;
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };

    /**
     * 计算两指之间的距离
     */
    const getTouchDistance = (touch1: Touch, touch2: Touch) => {
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    /**
     * 更新视频缩放
     */
    const updateVideoZoom = () => {
      applyVideoTransform();
    };

    /**
     * 处理触摸开始事件
     */
    const handleTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;

      if (touchEvent.touches.length === 1) {
        touchTapStartX = touchEvent.touches[0].clientX;
        touchTapStartY = touchEvent.touches[0].clientY;
        touchTapMoved = false;
      }

      if (!props.isPortraitMode) return;

      if (touchEvent.touches.length === 1) {
        // 单指触摸 - 准备拖动窗口
        isTouchDragging.value = true;
        touchStartX.value =
          touchEvent.touches[0].clientX -
          (videoPanelRef.value?.offsetLeft || 0);
        touchStartY.value =
          touchEvent.touches[0].clientY - (videoPanelRef.value?.offsetTop || 0);
      } else if (touchEvent.touches.length === 2) {
        // 双指触摸 - 准备缩放视频
        isPinching.value = true;
        touchTapMoved = true;
        touchStartDistance.value = getTouchDistance(
          touchEvent.touches[0],
          touchEvent.touches[1]
        );
        touchStartZoom.value = currentZoom.value;
      }
    };

    /**
     * 处理触摸移动事件
     */
    const handleTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;

      if (touchEvent.touches.length === 1) {
        const dx = Math.abs(touchEvent.touches[0].clientX - touchTapStartX);
        const dy = Math.abs(touchEvent.touches[0].clientY - touchTapStartY);
        if (dx > 8 || dy > 8) {
          touchTapMoved = true;
        }
      }

      if (!props.isPortraitMode || !videoPanelRef.value) return;

      if (isTouchDragging.value && touchEvent.touches.length === 1) {
        // 单指拖动窗口
        const newX = touchEvent.touches[0].clientX - touchStartX.value;
        const newY = touchEvent.touches[0].clientY - touchStartY.value;

        // 限制在窗口范围内
        const maxX = window.innerWidth - (videoPanelRef.value.offsetWidth || 0);
        const maxY =
          window.innerHeight - (videoPanelRef.value.offsetHeight || 0);

        const clampedX = Math.max(0, Math.min(maxX, newX));
        const clampedY = Math.max(0, Math.min(maxY, newY));

        // 使用 requestAnimationFrame 优化渲染性能
        requestAnimationFrame(() => {
          videoPanelRef.value!.style.left = `${clampedX}px`;
          videoPanelRef.value!.style.top = `${clampedY}px`;
        });
      } else if (isPinching.value && touchEvent.touches.length === 2) {
        // 双指缩放视频
        const currentDistance = getTouchDistance(
          touchEvent.touches[0],
          touchEvent.touches[1]
        );
        const scale = currentDistance / touchStartDistance.value;
        currentZoom.value = Math.max(
          0.5,
          Math.min(3, touchStartZoom.value * scale)
        );

        // 使用 requestAnimationFrame 优化渲染性能
        requestAnimationFrame(() => {
          updateVideoZoom();
        });
      }
    };

    /**
     * 处理触摸结束事件
     */
    const handleTouchEnd = (e: Event) => {
      const wasPinching = isPinching.value;
      const wasDragging = isTouchDragging.value && touchTapMoved;
      isTouchDragging.value = false;
      isPinching.value = false;

      // 轻触（非拖动/缩放）时旋转画面
      if (
        props.isConnected &&
        !props.mountOnly &&
        !wasPinching &&
        !wasDragging &&
        !touchTapMoved &&
        !dragMoved.value
      ) {
        suppressClickUntil = Date.now() + 450;
        cycleRotation();
      }
    };

    const observeLocalPlayerVideo = (): void => {
      const player = document.querySelector('#local-player');
      if (!player) {
        return;
      }

      videoAttachObserver?.disconnect();
      videoAttachObserver = new MutationObserver((mutations) => {
        const videoAdded = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof HTMLVideoElement ||
              (node instanceof HTMLElement && node.querySelector('video'))
          )
        );
        if (videoAdded) {
          applyVideoTransform();
        }
      });
      videoAttachObserver.observe(player, {
        childList: true,
        subtree: true,
      });

      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(() => {
        applyVideoTransform();
      });
      resizeObserver.observe(player);

      applyVideoTransform();
    };

    /**
     * 重置视频面板样式到横屏模式
     */
    const resetToLandscape = () => {
      if (videoPanelRef.value) {
        // 恢复默认样式
        videoPanelRef.value.style.position = '';
        videoPanelRef.value.style.left = '';
        videoPanelRef.value.style.top = '';
        videoPanelRef.value.style.width = '';
        videoPanelRef.value.style.height = '';
        videoPanelRef.value.style.zIndex = '';
      }

      // 重置缩放
      currentZoom.value = 1;
      updateVideoZoom();
    };

    /**
     * 组件挂载时初始化
     */
    onMounted(() => {
      if (props.mountOnly) {
        return;
      }

      if (props.isPortraitMode && videoPanelRef.value) {
        // 初始化位置和大小
        videoPanelRef.value.style.position = 'absolute';
        videoPanelRef.value.style.left = '20px';
        videoPanelRef.value.style.top = '20px';
        videoPanelRef.value.style.width = '200px';
        videoPanelRef.value.style.height = '150px';
        videoPanelRef.value.style.zIndex = '100';
      } else {
        // 确保横屏模式下恢复默认样式
        resetToLandscape();
      }

      // 添加触摸事件监听器
      const videoContainer =
        videoPanelRef.value?.querySelector('.video-container');
      if (videoContainer) {
        videoContainer.addEventListener('touchstart', handleTouchStart, {
          passive: false,
        });
        videoContainer.addEventListener('touchmove', handleTouchMove, {
          passive: false,
        });
        videoContainer.addEventListener('touchend', handleTouchEnd);
        videoContainer.addEventListener('touchcancel', handleTouchEnd);
      }

      observeLocalPlayerVideo();
      window.addEventListener('resize', applyVideoTransform);
    });

    watch(
      () => props.isConnected,
      (connected) => {
        if (connected) {
          // RTC 注入 video 可能稍有延迟
          window.setTimeout(() => {
            observeLocalPlayerVideo();
            applyVideoTransform();
          }, 300);
          window.setTimeout(applyVideoTransform, 1000);
        }
      }
    );

    /**
     * 监听屏幕方向变化
     */
    watch(
      () => [props.isPortraitMode, props.mountOnly] as const,
      ([portrait, mountOnly]) => {
        if (mountOnly) {
          resetToLandscape();
          if (videoPanelRef.value) {
            videoPanelRef.value.style.position = '';
            videoPanelRef.value.style.left = '';
            videoPanelRef.value.style.top = '';
            videoPanelRef.value.style.width = '';
            videoPanelRef.value.style.height = '';
            videoPanelRef.value.style.zIndex = '';
          }
          return;
        }

        if (!portrait) {
          resetToLandscape();
        } else if (videoPanelRef.value) {
          videoPanelRef.value.style.position = 'absolute';
          videoPanelRef.value.style.left = '20px';
          videoPanelRef.value.style.top = '20px';
          videoPanelRef.value.style.width = '200px';
          videoPanelRef.value.style.height = '150px';
          videoPanelRef.value.style.zIndex = '100';
        }
      }
    );

    /**
     * 组件卸载时清理
     */
    onUnmounted(() => {
      document.removeEventListener('mousemove', drag);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
      window.removeEventListener('resize', applyVideoTransform);
      videoAttachObserver?.disconnect();
      videoAttachObserver = null;
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (rotationHintTimer !== null) {
        window.clearTimeout(rotationHintTimer);
        rotationHintTimer = null;
      }

      // 移除触摸事件监听器
      const videoContainer =
        videoPanelRef.value?.querySelector('.video-container');
      if (videoContainer) {
        videoContainer.removeEventListener('touchstart', handleTouchStart);
        videoContainer.removeEventListener('touchmove', handleTouchMove);
        videoContainer.removeEventListener('touchend', handleTouchEnd);
        videoContainer.removeEventListener('touchcancel', handleTouchEnd);
      }
    });

    return {
      videoPanelRef,
      startDrag,
      startResize,
      resetToLandscape,
      handleVideoClick,
      rotationDeg,
      rotationHintVisible,
    };
  },
});
</script>

<style scoped>
.video-panel {
  width: 50%;
  height: 100%;
  min-height: 460px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 8px 32px rgba(0, 80, 180, 0.08),
    0 0 30px rgba(0, 184, 217, 0.12),
    0 0 20px rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(0, 184, 217, 0.25);
  transition: all 0.3s ease;
}

.video-panel[style*='display: none'] {
  display: none !important;
}

.video-panel.mount-only {
  position: fixed !important;
  width: 1px !important;
  height: 1px !important;
  min-height: 0 !important;
  left: -9999px !important;
  top: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
  overflow: hidden !important;
  z-index: -1 !important;
}

/* 竖屏模式下的视频面板样式 */
.video-panel.portrait-mode {
  position: absolute;
  width: 200px;
  height: 150px;
  min-height: unset;
  cursor: move;
  box-shadow:
    0 12px 40px rgba(0, 80, 180, 0.1),
    0 0 30px rgba(0, 184, 217, 0.15),
    0 0 20px rgba(124, 58, 237, 0.1);
  z-index: 100;
}

/* 视频控制面板 */
.video-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

/* 调整大小手柄 */
.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: rgba(0, 184, 217, 0.15);
  cursor: nwse-resize;
  border-bottom-right-radius: 16px;
  border-top: 1px solid rgba(0, 184, 217, 0.15);
  border-left: 1px solid rgba(0, 184, 217, 0.15);
  z-index: 11;
}

.resize-handle:hover {
  background: rgba(0, 184, 217, 0.3);
}

.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.video-container.can-rotate {
  cursor: pointer;
}

.video-container :deep(#local-player) {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px;
}

.video-container :deep(#local-player video) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
  /* 镜像由 JS 与旋转一并控制，避免 !important 覆盖角度调整 */
  transform: scaleX(-1);
  transform-origin: center center;
}

.rotation-hint {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 12;
  min-width: 64px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: center;
  pointer-events: none;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.video-placeholder {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--text-tertiary, #7c8db5);
  font-size: 16px;
  width: 100%;
  height: 100%;
  background: rgba(240, 245, 255, 0.8);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.camera-off-icon {
  font-size: 64px;
  color: var(--text-secondary, #475569);
}

.minimize-video-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 184, 217, 0.2);
  color: var(--text-primary, #1a2340);
}

.minimize-video-btn:hover {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-color: rgba(0, 184, 217, 0.35);
}

/* 用户说话时的红色边框效果 */
.video-panel.user-speaking {
  border: 2px solid #ef4444 !important;
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.4) !important;
  animation: pulse-red 1.5s ease-in-out infinite alternate;
}

@keyframes pulse-red {
  0% {
    box-shadow: 0 0 15px rgba(239, 68, 68, 0.3);
  }
  100% {
    box-shadow: 0 0 25px rgba(239, 68, 68, 0.6);
  }
}
</style>
